import * as Sentry from "@sentry/browser";

// Simple error logging fallback for CSP-restricted environments
const logErrorLocally = (error, context = {}) => {
  const errorData = {
    message: error.message || 'Unknown error',
    stack: error.stack,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    context
  };

  console.group('[Sentry] Error captured locally');
  console.error('Error:', error);
  console.log('Context:', context);
  console.log('Full data:', errorData);
  console.groupEnd();

  // Try to store in sessionStorage (more reliable than localStorage in CSP)
  try {
    const key = 'sentry_errors';
    const stored = JSON.parse(sessionStorage.getItem(key) || '[]');
    stored.push(errorData);
    sessionStorage.setItem(key, JSON.stringify(stored.slice(-20))); // Keep last 20
  } catch (e) {
    // Ignore storage errors
  }
};

const SENTRY_CONFIG = {
  dsn: "https://20a7e2e122d90d156cf3b1ce7201a05c@o4509711736963072.ingest.de.sentry.io/4509996338053200",
  environment: 'production',
  debug: false,
  tracesSampleRate: 0,
  autoSessionTracking: false,
  // Minimal integrations only
  integrations: [],
  beforeSend(event) {
    // Filtrar errores de extensiones del navegador
    if (event.exception) {
      const error = event.exception.values[0];
      if (error.stacktrace && error.stacktrace.frames) {
        const lastFrame = error.stacktrace.frames[error.stacktrace.frames.length - 1];
        if (lastFrame && lastFrame.filename && lastFrame.filename.includes('extension://')) {
          return null;
        }
      }

      // Add context for SyntaxErrors to help with debugging
      if (error.type === 'SyntaxError') {
        event.contexts = event.contexts || {};
        event.contexts.syntax_error = {
          location: window.location.href,
          error_message: error.value,
          timestamp: new Date().toISOString()
        };
      }
    }
    return event;
  },
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
};

let isSentryEnabled = false;

export const initSentry = () => {
  // Check if Sentry is enabled via build configuration
  if (typeof window.SENTRY_ENABLED !== 'undefined' && !window.SENTRY_ENABLED) {
    console.log('[Sentry] Disabled via build configuration');
    return;
  }

  try {
    // Try to initialize Sentry
    Sentry.init(SENTRY_CONFIG);

    // Set user context for Shopify
    if (window.Shopify && window.Shopify.shop) {
      Sentry.setContext("shop", {
        shop_domain: window.Shopify.shop,
        currency: window.Shopify.currency?.active || 'EUR',
        country: window.Shopify.country || 'ES',
      });
    }

    // Set custom tags
    Sentry.setTag("theme", "mediamarkt-canarias");

    isSentryEnabled = true;
    console.log('[Sentry] Initialized successfully');
  } catch (error) {
    console.warn('[Sentry] Failed to initialize, using local logging fallback');
    isSentryEnabled = false;
  }
};

export const captureError = (error, context = {}) => {
  // Check if Sentry is disabled via build configuration
  if (typeof window.SENTRY_ENABLED !== 'undefined' && !window.SENTRY_ENABLED) {
    logErrorLocally(error, context);
    return;
  }

  if (isSentryEnabled) {
    try {
      Sentry.captureException(error, {
        contexts: { custom: context }
      });
    } catch (e) {
      logErrorLocally(error, context);
    }
  } else {
    logErrorLocally(error, context);
  }
};

export const captureMessage = (message, level = 'info', context = {}) => {
  // Check if Sentry is disabled via build configuration
  if (typeof window.SENTRY_ENABLED !== 'undefined' && !window.SENTRY_ENABLED) {
    console.log(`[Sentry] ${level}: ${message}`, context);
    return;
  }

  if (isSentryEnabled) {
    try {
      Sentry.captureMessage(message, level, {
        contexts: { custom: context }
      });
    } catch (e) {
      console.log(`[Sentry] ${level}: ${message}`, context);
    }
  } else {
    console.log(`[Sentry] ${level}: ${message}`, context);
  }
};

export const addBreadcrumb = (message, category = 'custom', level = 'info', data = {}) => {
  // Check if Sentry is disabled via build configuration
  if (typeof window.SENTRY_ENABLED !== 'undefined' && !window.SENTRY_ENABLED) {
    console.log(`[Sentry] Breadcrumb: ${message}`, { category, level, data });
    return;
  }

  if (isSentryEnabled) {
    try {
      Sentry.addBreadcrumb({ message, category, level, data });
    } catch (e) {
      console.log(`[Sentry] Breadcrumb: ${message}`, { category, level, data });
    }
  } else {
    console.log(`[Sentry] Breadcrumb: ${message}`, { category, level, data });
  }
};

// Helper to get stored errors for debugging
export const getStoredErrors = () => {
  try {
    return JSON.parse(sessionStorage.getItem('sentry_errors') || '[]');
  } catch (e) {
    return [];
  }
};

// Helper to clear stored errors
export const clearStoredErrors = () => {
  try {
    sessionStorage.removeItem('sentry_errors');
    console.log('[Sentry] Cleared stored errors');
  } catch (e) {
    // Ignore
  }
};

export { Sentry };